import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { Campaign, CampaignType } from '../types';

export interface CampaignListFilters {
  type?: CampaignType;
  brand?: string;
}

const listKey = (f?: CampaignListFilters) => ['campaigns', f ?? {}] as const;
const oneKey = (id: string) => ['campaign', id] as const;

const listCampaigns = async (f?: CampaignListFilters): Promise<Campaign[]> => {
  const params = new URLSearchParams();
  if (f?.type) params.set('type', f.type);
  if (f?.brand) params.set('brand', f.brand);
  const q = params.toString() ? `?${params.toString()}` : '';
  const res = await axios.get(`api/campaigns/${q}`);
  return res.data;
};

export const getCampaign = async (id: string): Promise<Campaign> => {
  const res = await axios.get(`api/campaigns/${id}/`);
  return res.data;
};

export const useCampaigns = (f?: CampaignListFilters) =>
  useQuery({ queryKey: listKey(f), queryFn: () => listCampaigns(f) });

export const useCampaign = (id: string | undefined) =>
  useQuery({ queryKey: oneKey(id ?? ''), queryFn: () => getCampaign(id as string), enabled: !!id });

export interface CreateCampaignInput {
  id: string;
  name: string;
  emoji?: string;
  type: CampaignType;
  /** Optional — null = lands in the "To plan" group. */
  start_date?: string | null;
  end_date?: string | null;
  description?: string;
  parent?: string | null;
  brands?: string[];
}

export const useCreateCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCampaignInput): Promise<Campaign> => {
      const res = await axios.post('api/campaigns/', payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
};

export const useUpdateCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Campaign> }): Promise<Campaign> => {
      const res = await axios.patch(`api/campaigns/${id}/`, patch);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.setQueryData(oneKey(data.id), data);
    },
  });
};

export const useClaimCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Campaign> => {
      const res = await axios.post(`api/campaigns/${id}/claim/`);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.setQueryData(oneKey(data.id), data);
    },
  });
};

export const useDeleteCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`api/campaigns/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
};

export interface DuplicateCampaignInput {
  name: string;
  start_date: string;
  end_date: string;
  asset_ids: number[];
}

export const useDuplicateCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceId,
      payload,
    }: {
      sourceId: string;
      payload: DuplicateCampaignInput;
    }): Promise<Campaign> => {
      const res = await axios.post(
        `api/campaigns/${sourceId}/duplicate/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
};

/** Turn on one brand's share link (brand null = whole campaign), optionally
 *  setting the intro at the top of its page. Other brands' links are untouched. */
export const useShareCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, brand, intro }: { id: string; brand: string | null; intro?: string }): Promise<Campaign> => {
      const res = await axios.post(`api/campaigns/${id}/share/`, intro === undefined ? { brand } : { brand, intro });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      // The detail page reads ['campaign', id] — update it directly so the
      // share modal immediately sees the link and the Stop-sharing button.
      qc.setQueryData(oneKey(updated.id), updated);
    },
  });
};

/** Turn off one brand's share link (its URL stops working). */
export const useUnshareCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, brand }: { id: string; brand: string | null }): Promise<Campaign> => {
      const res = await axios.post(`api/campaigns/${id}/unshare/`, { brand });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.setQueryData(oneKey(updated.id), updated);
    },
  });
};

/** Payload of the anonymous shared-campaign page — allowlisted fields only. */
export interface SharedPlacement {
  category: string;
  asset_type: string;
  label: string;
  publish_date: string;
  end_date: string | null;
  publish_time: string | null;
  /** Proof of delivery, empty until uploaded. */
  proof_url: string;
  proof_screenshot: string;
  proof_extra_screenshots?: string[];
  /** Brand link in a multi-brand campaign: true = this spot is the brand's
   *  alone, false = shared with other brands. null = no label. */
  just_for_brand: boolean | null;
  /** Key into `previews` for the hover picture, null = none. */
  preview?: string | null;
  /** Brand plan pages: index into `campaigns`. */
  campaign?: number;
}
/** What a spot looks like: a website strip with the spot framed (frame in %
 *  of the picture), or an example picture for off-site spots. */
export interface SharedPreview {
  image: string;
  width: number | null;
  height: number | null;
  frame: { x: number; y: number; w: number; h: number } | null;
  label: string;
}
export interface SharedCampaignData {
  name: string;
  emoji: string;
  start_date: string | null;
  end_date: string | null;
  description: string;
  brand: { id: string; name: string; country: string; color: string; accent: string; logo: string | null } | null;
  placements: SharedPlacement[];
  previews?: Record<string, SharedPreview>;
  /** Brand plan pages only: the campaigns the placements belong to. */
  campaigns?: { name: string; emoji: string; start_date: string | null; end_date: string | null }[];
}

export const fetchSharedCampaign = async (token: string): Promise<SharedCampaignData> => {
  const res = await axios.get(`api/public/campaigns/shared/${encodeURIComponent(token)}/`);
  return res.data;
};

export const fetchSharedBrandPlan = async (token: string): Promise<SharedCampaignData> => {
  const res = await axios.get(`api/public/brands/shared/${encodeURIComponent(token)}/`);
  return res.data;
};

/** A brand's plan link across campaigns (team side). */
export interface BrandShare {
  id: number;
  brand: string;
  token: string;
  enabled: boolean;
  title: string;
  intro: string;
  date_from: string | null;
  date_to: string | null;
  created_at: string;
}

const brandSharesKey = (brandId: string) => ['brand-shares', brandId];

export const useBrandShares = (brandId: string) =>
  useQuery({
    queryKey: brandSharesKey(brandId),
    queryFn: async (): Promise<BrandShare[]> =>
      (await axios.get('api/brand-shares/', { params: { brand: brandId } })).data,
    enabled: !!brandId,
  });

export const useSaveBrandShare = (brandId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<BrandShare>): Promise<BrandShare> =>
      id
        ? (await axios.patch(`api/brand-shares/${id}/`, body)).data
        : (await axios.post('api/brand-shares/', { ...body, brand: brandId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: brandSharesKey(brandId) }),
  });
};
